<!-- EXHAUSTIN's shell. Deliberately thin: the root, the outlet the router
     paints into, and the handful of things that must exist on every screen at
     once.

     The screens are in src/pages/, wired up in src/router.js. The game itself
     — the bank, the engine, the scoring, the puns — is in src/game/. -->
<template>
  <IonApp>
    <DemoBanner v-if="isDemo" />

    <IonRouterOutlet />

    <!-- The last card's button names the first real action, which is what the
         component's `finishLabel` is for. The third card used to carry a `cta`
         key instead — a prop Onboarding does not have — so the button silently
         read "Get started" and the line that was written for it never
         appeared. -->
    <Onboarding :cards="onboarding" finish-label="Pick a mode" />

    <Overlays />
  </IonApp>
</template>

<!-- The link vocabulary, declared at MODULE scope on purpose.

     `main.js` replays the URL the app was opened with — `app.route(location.href)`
     — before the first mount, and the bus keeps no history, so a declaration
     made inside `setup()` arrives after the only event it exists to catch. A
     plain <script> block runs when main.js imports this file, which is early
     enough. See core/deeplink.js. -->
<script>
import { defineLinks } from './core/ui';
import { PRODUCT_ROUTES } from './product-routes.js';

// WITHOUT THIS, EVERY LINK INTO THE APP OPENS THE MODE PICKER. The rule set
// ships empty, and an empty rule set resolves every URL to null: the Universal
// Link this app claims (`/open/...`, per app.config.json), the `/app?mode=...`
// somebody pastes to a mate, and every route the QA pass follows all land on
// Play and look like they worked.
//
// Validated against the app's own route table, which is the rule that matters:
// an id nothing knows resolves to null and the app opens Home. A link is user
// input — it gets shortened, pasted and mangled — and a not-found screen reads
// as a broken app where Home reads as a link that went stale.
//
// Results is left out. It reports on a run that happened on this device, so a
// link to it can only ever arrive at its empty state.
const LINKABLE = new Set(
  PRODUCT_ROUTES.filter((r) => r.id !== 'results').map((r) => r.id),
);

// The routes are registered under their ids, so the id IS the location.
const screen = (id) => (id && LINKABLE.has(id) ? { name: id } : null);

defineLinks([
  // Most specific first: an explicit parameter beats a path segment.
  {
    name: 'screen',
    match: ({ params }) => screen(params.get('mode') ?? params.get('screen')),
    href: ({ mode }) => (LINKABLE.has(mode) ? `/app?mode=${encodeURIComponent(mode)}` : null),
  },
  {
    // `/open/last-gasp`, and `/app/last-gasp` for the same reason — the path
    // people actually paste, not only the query. Anything else falls through.
    name: 'open',
    match: ({ pathname }) => screen(pathname.split('/').filter(Boolean).pop()),
    href: ({ mode }) => (LINKABLE.has(mode) ? `/open/${encodeURIComponent(mode)}` : null),
  },
]);
</script>

<script setup>
import { onMounted, watch } from 'vue';
import { IonApp, IonRouterOutlet } from '@ionic/vue';
import { Onboarding, DemoBanner, Overlays, isDemo } from './core/ui';
import { ensureProfile, profile } from './game/profile.js';
import { setHaptics } from './core/util.js';

// Three cards, one promise each, in his words — not a feature tour. The last
// one lands on the thing the app is actually for.
//
// The bodies are kept under ninety characters deliberately. Onboarding's
// content does not scroll (`IonContent :scroll-y="false"`), so a card that
// needs six lines at 200% reading size on a 5" phone does not get cut off —
// it pushes the one full-width button off the bottom of the screen, and the
// first screen of the app becomes one with no way forward.
const onboarding = [
  {
    title: 'EXHAUSTIN',
    body: 'Trivia until you drop. Seven ways to play, all of them fast, none of them gentle.',
  },
  {
    title: 'Last as long as you can',
    body: 'It never stops, it only speeds up. Three lives, and your score is how long you lasted.',
  },
  {
    title: 'Watch for the AUS BONUS',
    body: 'AUSTRALIA or AUSTRIA is the answer? The screen goes off and the points double.',
  },
];

onMounted(async () => {
  // The single local record has to exist before any screen reads a total off it.
  await ensureProfile();

  // AND THE HAPTICS PREFERENCE HAS TO BE APPLIED TO THE THING THAT HONOURS IT.
  //
  // `haptic()` in core/util.js checks a module-level flag that only
  // `setHaptics()` moves, and nothing in the app was calling it. So the
  // `haptics` field on the profile was a stored preference that did nothing at
  // all: turning it off left every buzz exactly where it was. That is the
  // worst kind of settings bug, because the control looks like it worked.
  setHaptics(profile.value.haptics !== false);
});

// Kept in step, so the toggle on the Topics screen takes effect on the next tap
// rather than the next launch.
watch(() => profile.value.haptics, (on) => setHaptics(on !== false));
</script>
